import type { PillProps } from 'ext-ui/components/Pill/Pill'
import { useTranslate } from 'src/localization/Trns'
import type {
  StatementPaymentMethod,
  StatementPaymentStatus,
  StatementStatus,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'

/**
 * Display labels for the Card Management status and method vocabularies.
 *
 * These are CLIENT-authored, unlike Activity's `message`/`detail`, which the
 * server writes. The statement and payment contracts expose closed enums with
 * no display strings attached, so the copy and its localization belong here.
 *
 * Every mapping below is exhaustive over its union rather than defaulted, so
 * adding a contract value is a typecheck failure at this file instead of a
 * SCREAMING_SNAKE token leaking onto the page.
 */

/** "Paid" / "Open" / "Past due" — the pill text on a statement row. */
export const useStatementStatusLabel = (status: StatementStatus): string => {
  const translate = useTranslate()

  const labels: Record<StatementStatus, string> = {
    DUE: translate('Open'),
    PARTIALLY_PAID: translate('Partially paid'),
    PAID: translate('Paid'),
    PAST_DUE: translate('Past due'),
  }

  return labels[status]
}

/**
 * Pill colour for a statement status.
 *
 * Colour is never the only signal — every pill also carries the label above
 * (`src/CLAUDE.md`: do not use colour as the only means of conveying state).
 */
export const statementStatusPillColor = (
  status: StatementStatus,
): PillProps['color'] => {
  const colors: Record<StatementStatus, PillProps['color']> = {
    DUE: 'pending',
    PARTIALLY_PAID: 'pending',
    PAID: 'saas',
    PAST_DUE: 'severe',
  }

  return colors[status]
}

/** "Bank transfer" / "Wire" — the method named in a payment-history row. */
export const usePaymentMethodLabel = (
  method: StatementPaymentMethod,
): string => {
  const translate = useTranslate()

  const labels: Record<StatementPaymentMethod, string> = {
    BANK_TRANSFER: translate('Bank transfer'),
    CHECK: translate('Check'),
    WIRE: translate('Wire'),
    // The contract's catch-all for an issuer method that does not map safely to
    // a more specific public type.
    OTHER: translate('Other'),
  }

  return labels[method]
}

/** "Paid" / "Pending" / "Failed" / "Reversed" — a payment record's own status. */
export const usePaymentStatusLabel = (
  status: StatementPaymentStatus,
): string => {
  const translate = useTranslate()

  const labels: Record<StatementPaymentStatus, string> = {
    PENDING: translate('Pending'),
    PAID: translate('Paid'),
    FAILED: translate('Failed'),
    REVERSED: translate('Reversed'),
  }

  return labels[status]
}

export const paymentStatusPillColor = (
  status: StatementPaymentStatus,
): PillProps['color'] => {
  const colors: Record<StatementPaymentStatus, PillProps['color']> = {
    PENDING: 'pending',
    PAID: 'saas',
    FAILED: 'severe',
    REVERSED: 'severe',
  }

  return colors[status]
}
